// Pure helpers for the per-title support workspace (/support/:pub).
import type { InboundAttachment } from './replies'
import { TITLE_LABELS, type DeskBucket, type DeskChannel, type DeskConversation, type DeskState, type SavedReply, type StaffMember } from './supportDesk'

export type DeskSort = 'oldest' | 'cancel' | 'newest'

export const SORT_OPTIONS: { value: DeskSort; label: string }[] = [
  { value: 'oldest', label: 'Oldest first' },
  { value: 'cancel', label: 'Cancellations first' },
  { value: 'newest', label: 'Newest first' },
]

/** GET /api/support/conversations/{channel}/{id}: the list row plus the original message. */
export type DeskConversationDetail = DeskConversation & {
  body: string
  subject: string | null
  /** Set for a phone call forwarded from the Subscriber Desk: "Phone call · forwarded by …". */
  source_label?: string | null
  attachments?: InboundAttachment[]
}

export type ConversationRef = { channel: DeskChannel; id: number }

/** `?c=contact-41` → {channel: 'contact', id: 41}; anything else → null. */
export function parseConversationParam(s: string | null | undefined): ConversationRef | null {
  const m = /^(contact|feedback|email)-([1-9][0-9]{0,15})$/.exec(s ?? '')
  if (!m) return null
  const id = Number(m[2])
  return Number.isSafeInteger(id) ? { channel: m[1] as DeskChannel, id } : null
}

export function conversationParam(channel: DeskChannel, id: number): string {
  return `${channel}-${id}`
}

export function isDeskBucket(pub: string | undefined): pub is DeskBucket {
  return !!pub && pub in TITLE_LABELS
}

const SOURCES: Record<string, string> = {
  simplecirc: 'SimpleCirc', paypal: 'PayPal', stripe: 'Stripe (card)', authnet: 'Authorize.Net',
  manual: 'Manual', offline: 'Offline', gift: 'Gift',
}

/** Who bills a membership (subscription_source), as staff read it. Unknown sources show raw. */
export function sourceLabel(source: string, instrument: string): string {
  if (source === 'authnet' && instrument === 'bank') return 'Authorize.Net eCheck'
  return SOURCES[source] ?? (source || 'Unknown')
}

/** Only Stripe- and Authorize.Net-billed memberships have their payments in AAMCirc. */
export function paymentsKeptHere(source: string): boolean {
  return source === 'stripe' || source === 'authnet'
}

/** PayPal and SimpleCirc bill outside AAMCirc, so their payments live there. */
export function paymentsKeptElsewhere(source: string): boolean {
  return source === 'paypal' || source === 'simplecirc'
}

/** The queue order a tab opens with: oldest first while reviewing, newest activity first elsewhere. */
export function defaultSort(state: DeskState): DeskSort {
  return state === 'to_review' ? 'oldest' : 'newest'
}

/** An earlier conversation opens in its own title's workspace (or Needs a title). */
export function earlierHref(e: { channel: DeskChannel; id: number; publication: string | null }): string {
  return `/support/${e.publication ?? 'none'}?c=${conversationParam(e.channel, e.id)}`
}

export function centsLabel(cents: number | null): string {
  if (cents === null || cents === undefined) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

/** The Assign-to list with the signed-in staff member first. */
export function staffMeFirst(staff: StaffMember[], meEmail: string | undefined): StaffMember[] {
  const me = meEmail?.toLowerCase()
  const i = me ? staff.findIndex(s => s.email.toLowerCase() === me) : -1
  return i < 0 ? staff : [staff[i], ...staff.slice(0, i), ...staff.slice(i + 1)]
}

export function filterSavedReplies(list: SavedReply[], q: string): SavedReply[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return list
  return list.filter(r => r.name.toLowerCase().includes(needle) || r.body_md.toLowerCase().includes(needle))
}
